<script setup lang="ts">
import { watch } from 'vue';
import type { Props, Region } from './types';
import Select from '@/components/forms/select/select.vue';
import Button from '@/components/forms/button/button.vue';
import { useSelectorRegion } from './composables/selectorRegion';
import { dummyIndex, globalRegionIndex, searhcerCompareKey } from './utils/consts';
import { dialogRegionSelector } from '@/components/project/regionSelector/utils/utils';

const props = withDefaults(defineProps<Props>(), {
	searchers: () => [],
	// compareRegionsIndexes: () => [],
	addSearcherIcon: true,
	addRegionIcon: true,
	addChanger: true,
});

const model = defineModel<Props['modelValue']>({ required: true });
const modelSingle = defineModel<Props['modelValueSingle']>('modelValueSingle');

const {
	selectSearcher,
	selectRegion,
	compare,

	searcherByKey,
	allRegionsIndexes,

	getSearcher,
	getRegion,
} = useSelectorRegion(props);

const onClickCompare = () => {
	const regions: Region[] = [];

	searcherByKey.value.forEach((searcher) => {
		if (!searcher.enabled) return;

		searcher.regions.forEach((region) => {
			if (!region.enabled) return;

			regions.push(region);
		});
	});

	dialogRegionSelector.open('regions', {
		regions,
		regionsIndexes: compare.regionsIndexes.value,
		'@update:regionsIndexes': (regionsIndexes: number[]) => compare.regionsIndexes.value = regionsIndexes,
	});
};

watch([selectRegion.regionIndex, selectSearcher.searcherKey, compare.regionsIndexes], () => {
	if (selectSearcher.searcherKey.value === searhcerCompareKey && compare.regionsIndexes.value.length) {
		if (JSON.stringify(model.value) === JSON.stringify(compare.regionsIndexes.value)) {
			return;
		}

		model.value = [...compare.regionsIndexes.value];
	} else {
		if (props.onlySearcher) {
			model.value = [selectSearcher.searcherKey.value];

			if (selectSearcher.searcherKey.value === dummyIndex && !props.autoRegion) model.value.length = 0;
		} else {
			model.value = [selectRegion.regionIndex.value];

			// не добавлять несуществующий регион в результаты
			if (selectRegion.regionIndex.value === dummyIndex && !props.autoRegion) model.value.length = 0;
		}
	}

	// регионов нет
	if (!props.onlySearcher && !allRegionsIndexes.value.size) {
		selectSearcher.searcherKey.value = dummyIndex;
	}
});

if (modelSingle.value) {
	watch(modelSingle, () => {
		if (modelSingle.value) {
			model.value = [modelSingle.value];
		}
	}, { immediate: true });
}

watch(model, () => {
	if (model.value[0]) {
		modelSingle.value = model.value[0];
	}

	// проверка входных данных v-model на корректность
	if (props.onlySearcher) {
		if (
			!model.value.length ||
			model.value.length === 1 && !searcherByKey.value.has(model.value[0]) ||
			model.value.length === 1 && model.value[0] === dummyIndex && !props.autoRegion
		) {
			let defaultKey: number | undefined = searcherByKey.value.keys().next().value;

			// запретить устанавливать несуществующий регион
			if (defaultKey === dummyIndex && !props.autoRegion) {
				defaultKey = undefined;

				modelSingle.value = dummyIndex;
			}

			if (defaultKey !== undefined) {
				model.value = [defaultKey];
			}else{
				model.value.length = 0;
			}

			return;
		}

		if (
			model.value.length > 1 &&
			JSON.stringify(model.value) !== JSON.stringify(compare.regionsIndexes.value)
		) {
			model.value = [...compare.regionsIndexes.value];

			return;
		}
	} else {
		let newModel = [...new Set(model.value)];

		let defaultIndex = searcherByKey.value.values().next().value?.regionByIndex?.keys().next().value;
		if (props.forFrequency) {
			defaultIndex = searcherByKey.value.values().next().value?.regionByIndex?.values().next().value?.key;
		}

		// запретить устанавливать несуществующий регион
		if (defaultIndex === dummyIndex && !props.autoRegion) {
			defaultIndex = undefined;

			modelSingle.value = dummyIndex;
		}

		if (!newModel.length) {
			if (defaultIndex !== undefined) {
				newModel.push(defaultIndex);
			}
		} else if (newModel.length === 1) {
			let all = allRegionsIndexes.value;

			if (props.forFrequency) {
				all = new Set();
				searcherByKey.value.forEach((searcher) => {
					searcher.regionByIndex.forEach((region) => {
						if (region.index === globalRegionIndex) return;
						if (region.index === dummyIndex) return;

						all.add(region.key);
					});
				});
			}

			// убрано, так как dummyIndex запрещено выбирать в model
			// if (!all.has(newModel[0]) && newModel[0] !== dummyIndex) {
			if (!all.has(newModel[0])) {
				newModel = [];
				if (defaultIndex !== undefined) {
					newModel.push(defaultIndex);
				}
			}
		} else {
			newModel = newModel.filter(index => allRegionsIndexes.value.has(index));
			if (!newModel.length && defaultIndex !== undefined) {
				newModel.push(defaultIndex);
			}
		}

		if (JSON.stringify(model.value) !== JSON.stringify(newModel)) {
			model.value = newModel;

			return;
		}
	}

	// входные данные v-model совпадают с внутренними значениями
	if (
		model.value.length === 1 &&
		model.value[0] === (props.onlySearcher ? selectSearcher.searcherKey.value : selectRegion.regionIndex.value)
	) {
		return;
	}

	// if (
	// 	model.value.length > 1 &&
	// 	selectorSearcher.searcherKey.value === searhcerCompareKey &&
	// 	JSON.stringify(model.value) === JSON.stringify(selectorCompare.regionsIndexes.value)
	// ) {
	// 	return;
	// }

	// обновление regionIndex, searcherKey, selectorCompare.regionsIndexes
	if (props.onlySearcher) {
		// if (!model.value.length) {
		// 	selectorSearcher.searcherKey.value = dummyIndex;
		//
		// 	return;
		// }

		if (model.value.length === 1) {
			selectSearcher.searcherKey.value = model.value[0];

			return;
		}

		selectSearcher.searcherKey.value = searhcerCompareKey;

		return;
	} else {
		if (!model.value.length) {
			// selectSearcher.searcherKey.value = dummyIndex;
			// selectRegion.regionIndex.value = dummyIndex;

			return;
		}

		if (model.value.length === 1 && selectSearcher.searcherKey.value !== searhcerCompareKey) {
			selectRegion.regionIndex.value = model.value[0];

			let newSearherKey: number | undefined;
			for (const searcher of searcherByKey.value.values()) {
				for (const region of searcher.regionByIndex.values()) {
					const currentRegionIndex = props.forFrequency ? region.key : region.index;
					if (currentRegionIndex === selectRegion.regionIndex.value) {
						newSearherKey = searcher.key;

						break;
					}
				}

				if (newSearherKey !== undefined) {
					break;
				}
			}

			if (newSearherKey !== undefined) {
				selectSearcher.searcherKey.value = newSearherKey;
			}
		} else {
			selectSearcher.searcherKey.value = searhcerCompareKey;
			compare.regionsIndexes.value = [...model.value];
		}
	}
}, { immediate: true });

defineExpose({
	getSearcher,
	getRegion,
});
</script>

<template>
	<div
		:class="{
			'top-selectorRegion': true,
			'top-selectorRegion-onlySearcher': onlySearcher,
		}"
	>
		<Select
			:options="selectSearcher.optionBySearcherKey.value"
			v-model="selectSearcher.searcherKey.value"
			name="searcher_key"
			:addChanger="addChanger"
		/>

		<Select
			v-if="!onlySearcher && selectSearcher.searcherKey.value !== searhcerCompareKey"
			class="top-select-region"
			:options="selectRegion.optionByRegionIndex.value"
			v-model="selectRegion.regionIndex.value"
			:name="forFrequency ? 'region_key' : 'region_index'"
			:addChanger="addChanger"
			:data-top-icon="addRegionIcon ? '' : undefined"
		/>

		<Button
			v-if="addCompare && !onlySearcher && selectSearcher.searcherKey.value === searhcerCompareKey"
			name="compare"
			@click="onClickCompare"
			:data-count-compare-regions-indexes="compare.regionsIndexes.value.length"
		>
			{{ $i18n.Common.Selected_regions }}
		</Button>
	</div>
</template>

<style>
@import "./styles/searcherColors.css";

.top-selectorRegion {
	width: 340px;
	display: inline-flex;
	vertical-align: middle;
}

.top-selectorRegion > .top-select {
	flex-grow: 1;
}

.top-selectorRegion > .top-select:focus-within {
	z-index: 4;
}

.top-selectorRegion > .top-select > .top-select_select:hover,
.top-selectorRegion > .top-select > .top-select_select.top-error {
	z-index: 1;
}

.top-selectorRegion > .top-select-searcher_key {
	width: 140px;
	max-width: 160px;
	margin-right: -1px;
}

.top-selectorRegion > .top-select-region > select {
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
}

.top-selectorRegion > .top-select-region {
	--top-icon-size: 20px;
	--top-icon-color: var(--color-text-primary);

	--top-icon2-size: 20px;
	--top-icon2-color: var(--color-text-primary);
}

.top-selectorRegion > .top-select-region[data-top-icon][data-top-icon2] {
	--top-icon2-size: 16px;
}

.top-selectorRegion > .top-select-region[data-top-icon][data-top-icon2]:after {
	text-indent: -4px;
}

.top-selectorRegion > [name="compare"] {
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
	flex-grow: 1;
}

.top-selectorRegion > [name="compare"]:after {
	content: "(" attr(data-count-compare-regions-indexes) ")";
	margin: 0 0 0 6px;
}

.top-selectorRegion:not(.top-selectorRegion-onlySearcher) > .top-select-searcher_key > select {
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
	margin-right: 0;
}

.top-selectorRegion:not(.top-selectorRegion-onlySearcher) > .top-select-searcher_key[data-value="-1"] > select {
	border-right: none;
}

.top-selectorRegion-onlySearcher {
	width: 140px;
}
</style>
